import { useState, type ReactElement, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  isPermissionRefused,
  type RoleAssignment,
  type RoleAssignmentWrite,
  type RoleAssignmentsResponse,
} from '@citibox-panel/api-client';
import { useAuthContext } from '@citibox-panel/auth';
import { Box } from '@citibox-panel/ui';
import { useIsArrival } from '@/lib/arrival';
import {
  fetchRoleAssignments as fetchRoleAssignmentsFromApi,
  removeAllRoleAssignment as removeAllRoleAssignmentFromApi,
  upsertRoleAssignment as upsertRoleAssignmentFromApi,
} from '@/lib/apiClient';
import { MARKET_LABELS, resolveMarket } from '@/lib/marketIndicator';
import { readRuntimeConfig } from '@/lib/runtimeConfig';
import { ROLE_ASSIGNMENTS_KEY, useRoleAssignments } from '../hooks/useRoleAssignments';
import { useSaveRoleAssignment, type SaveRequest } from '../hooks/useSaveRoleAssignment';
import { canonicalEmail } from '../lib/email';
import { RolesManagementPage, type RolesManagementPageProps } from './RolesManagementPage';

export interface RolesManagementRouteProps {
  // Injected by tests; production binds the `@/lib/apiClient` functions, which own the mock switch.
  fetchRoleAssignments?: () => Promise<RoleAssignmentsResponse>;
  upsertRoleAssignment?: (write: RoleAssignmentWrite) => Promise<void>;
  removeAllRoleAssignment?: (email: string, expectedVersion: string) => Promise<void>;
  // The space's band and navigation around the screen, handed by the route table.
  frame: (content: ReactNode) => ReactElement;
}

// Composition root of «Roles y permisos»: the market label, the read and the write. A refusal from
// the server is the host's to answer: it swaps this view out, frame included.
export function RolesManagementRoute({
  fetchRoleAssignments = fetchRoleAssignmentsFromApi,
  upsertRoleAssignment = upsertRoleAssignmentFromApi,
  removeAllRoleAssignment = removeAllRoleAssignmentFromApi,
  frame,
}: RolesManagementRouteProps): ReactElement {
  const { firebaseUser } = useAuthContext();
  const arrived = useIsArrival();
  const queryClient = useQueryClient();
  const query = useRoleAssignments(fetchRoleAssignments);
  const mutation = useSaveRoleAssignment({
    upsert: upsertRoleAssignment,
    removeAll: removeAllRoleAssignment,
  });

  // TanStack Query clears `error` and puts a query without data back to `pending` while it
  // refetches, so the last read failure is kept here: a «Reintentar» in flight stays on the failure,
  // busy, instead of falling back to «Cargando…» and dropping the keyboard on <body>.
  const [lastError, setLastError] = useState<unknown>(null);
  if (query.error !== null && query.error !== lastError) setLastError(query.error);

  const email = firebaseUser?.email ?? null;

  const market = resolveMarket(readRuntimeConfig());
  // The bare market for the copy («ES»), not the pill's text: null when it cannot be confirmed.
  const marketLabel = market === null ? null : MARKET_LABELS[market];

  // Data on screen wins over a failed refetch: the list stays readable while «Reintentar» runs.
  // Without data, the kept failure holds the error state through the retry.
  // A refusal paints no error of its own: the host swaps the view for it, so until then it loads.
  const readFailure = query.error ?? lastError;
  const status: RolesManagementPageProps['status'] =
    query.data !== undefined
      ? 'ready'
      : readFailure !== null && !isPermissionRefused(readFailure)
        ? 'error'
        : 'loading';

  const save = async (request: SaveRequest): Promise<void> => {
    await mutation.mutateAsync(request);
  };

  // «Cargar la versión actual»: a fresh read of the whole list (the resource has no per-person
  // read), which also refreshes the table behind the dialog. It must not inherit the client's
  // default retry (a 403 is never asked twice) nor pause offline (the dialog would stay locked).
  const reloadCurrent = async (target: string): Promise<RoleAssignment | null> => {
    const fresh = await queryClient.fetchQuery({
      queryKey: ROLE_ASSIGNMENTS_KEY,
      queryFn: fetchRoleAssignments,
      staleTime: 0,
      retry: false,
      networkMode: 'always',
    });
    const wanted = canonicalEmail(target);
    return fresh.assignments.find((assignment) => assignment.email === wanted) ?? null;
  };

  return frame(
    // On paper, not the page's gray: the muted subtitle and empty states clear AA only on white.
    <Box component="section" sx={{ p: 3, width: '100%', maxWidth: 1200, mx: 'auto', bgcolor: 'background.paper' }}>
      <RolesManagementPage
        data={query.data}
        status={status}
        onRetry={() => {
          void query.refetch();
        }}
        retrying={query.isFetching}
        selfEmail={email}
        marketLabel={marketLabel}
        save={save}
        pending={mutation.isPending}
        onReloadCurrent={reloadCurrent}
        focusHeadingOnArrival={arrived}
      />
    </Box>,
  );
}
